import { observer } from 'mobx-react-lite'

import Avatar from '@mui/material/Avatar'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Stack from '@mui/material/Stack'

import { NoteStack, Payments, Person } from '@mxenabled/mx-icons'

import { Text } from '@mxenabled/mxui'

import { StatusBar, StatusBarData, useGlobalCopyStore, useScreenSize } from '../../common'
import { type Goal, TrackType } from '../../core'

import { getGoalStatusData } from '../../core/utils/GoalsUtil'
import { formatCurrency } from '../../core/utils/NumberFormatting'

interface GoalStatusCardProps {
  goal: Goal
  isInDrawer?: boolean
  onClick?: () => void
}

const formatCurrencyForDeviceType = (amount: number, isMobileOrTablet: boolean) => {
  if (!isMobileOrTablet) return formatCurrency(amount, '0,0.00')
  return formatCurrency(amount, amount < 1000 ? '0' : '0.00a').replace(
    /(\d+)(?:\.0+|(\.\d*?[1-9])0*)([a-zA-Z]* ?\$?)/g,
    '$1$2$3',
  )
}

export const GoalStatusCard = observer(
  ({ goal, isInDrawer = false, onClick }: GoalStatusCardProps) => {
    const { goals: copy } = useGlobalCopyStore()
    const { isMobile, isTablet } = useScreenSize()

    const isMobileOrTablet = isMobile || isTablet

    const {
      backgroundColor: bgcolor,
      color,
      percentage,
      percentageText,
      projectedText,
    } = getGoalStatusData(copy, goal)

    const amount = goal.track_type === TrackType.DEBT_TRACK ? goal.initial_amount : goal.amount
    const data = [
      {
        color: bgcolor,
        percentage: `${percentage}`,
      },
    ] as StatusBarData[]

    let avatarIcon = <Payments />
    if (goal.track_type === TrackType.DEBT_TRACK) {
      avatarIcon = <NoteStack />
    } else if (goal.track_type === TrackType.RETIREMENT_TRACK) {
      avatarIcon = <Person />
    }

    return (
      <Card
        elevation={2}
        sx={{
          '&:focus-within': {
            outline: '2px solid',
            outlineColor: 'primary.main',
          },
        }}
      >
        <CardActionArea disabled={!onClick} onClick={onClick}>
          <CardHeader
            avatar={
              <Avatar sx={{ bgcolor, color, height: 48, width: 48 }} variant="rounded">
                {avatarIcon}
              </Avatar>
            }
            subheader={
              <Text
                sx={{
                  fontWeight: 700,
                  fontSize: isInDrawer || isMobileOrTablet ? '18px' : '23px',
                  lineHeight: isInDrawer || isMobileOrTablet ? '24px' : '32px',
                }}
              >
                {`${formatCurrency(goal.current_amount, isMobileOrTablet ? '0,0' : '0,0.00')} / ${formatCurrencyForDeviceType(
                  amount,
                  isMobileOrTablet,
                )}`}
              </Text>
            }
            sx={{ alignItems: 'flex-start', '& .MuiCardHeader-content': { minWidth: 0 } }}
            title={
              <Text
                color="text.secondary"
                fontWeight={400}
                sx={{
                  fontSize: '15px',
                  lineHeight: '20px',
                }}
                variant="h4"
              >
                {goal.name}
              </Text>
            }
          />
          <CardContent sx={{ pt: 0 }}>
            <Stack gap={6}>
              <StatusBar data={data} height={24} />
              <Stack alignItems="center" direction="row" justifyContent="space-between">
                <Text color="text.secondary" variant="caption">
                  {percentageText}
                </Text>
                {goal.projected_to_complete_at && (
                  <Text color="text.secondary" variant="caption">
                    {projectedText}
                  </Text>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    )
  },
)
